import { ClipboardList, History, LogOut, MailPlus, Menu as MenuIcon, Moon, PanelLeftClose, PanelLeftOpen, Plus, Settings, ShieldCheck, Sparkles, Sun, UserCog, Users } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth';
import { AssistantProvider, useAssistant } from './assistant/AssistantContext';
import { AssistantPanel } from './assistant/AssistantPanel';
import { Avatar } from './Avatar';
import { useI18n } from '../i18n';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useMedia } from './Pane';
import { useTheme } from '../theme';
import { RAIL_KEY, usePref, validZoom, ZOOM_KEY } from '../hooks/usePref';
import { PageLoader } from './ui';
import { roleLabel } from '../roles';

/** The YellowMetrics logo. Two files because the wordmark is black on light themes and white on dark (see .brand-logo in global.css). */
export function BrandLogo({ height = 26 }: { height?: number }) {
  return (
    <span className="brand-logo-wrap" role="img" aria-label="YellowMetrics">
      <img className="brand-logo on-light" src="/brand/logo.svg" alt="" height={height} />
      <img className="brand-logo on-dark" src="/brand/logo-white.svg" alt="" height={height} />
    </span>
  );
}

/** Just the yellow mark, for narrow spaces (collapsed sidebar). */
export function BrandMark({ size = 28 }: { size?: number }) {
  return <img className="brand-mark-img" src="/brand/logo-mark.svg" alt="" width={size} height={size} aria-hidden />;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}

/** Sidebar link to the Assistant page. Shown even before a model is configured, so admins can find the setup steps. */
function AssistantNavLink({ rail }: { rail: boolean }) {
  const { t } = useI18n();
  const { allowed } = useAssistant();
  if (!allowed) return null;
  return (
    <NavLink to="/assistant" className="nav-link" title={rail ? t('nav.assistant') : undefined}>
      <Sparkles /> <span className="nav-text">{t('nav.assistant')}</span>
    </NavLink>
  );
}

export function AppLayout() {
  const { user, logout, can } = useAuth();
  const { t } = useI18n();
  const [theme, setTheme] = useTheme();
  const [open, setOpen] = useState(false);
  // Collapsed icon rail for the main navigation, remembered per browser.
  // Ignored on small screens where the sidebar is an off-canvas drawer.
  const [rail, setRail] = usePref(RAIL_KEY, false);
  // Page zoom (Account → Display), applied only inside the signed-in app.
  const [zoomPref] = usePref(ZOOM_KEY, 1);
  const zoom = validZoom(zoomPref);
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--ui-zoom', String(zoom));
    return () => {
      root.style.removeProperty('--ui-zoom');
    };
  }, [zoom]);
  const narrow = useMedia('(max-width: 900px)');
  const railOn = rail && !narrow;
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  if (!user) return null;

  const dark =
    theme === 'dark' || (theme === 'system' && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
  const isManager = can('survey.view');
  const isAdmin = user.role === 'admin';
  const collapseButton = (
    <button
      type="button"
      className="icon-btn nav-rail-toggle"
      aria-pressed={railOn}
      aria-label={railOn ? t('nav.expand_sidebar') : t('nav.collapse_sidebar')}
      title={railOn ? t('nav.expand_sidebar') : t('nav.collapse_sidebar')}
      onClick={() => setRail((r) => !r)}
    >
      {railOn ? <PanelLeftOpen /> : <PanelLeftClose />}
    </button>
  );

  return (
    <AssistantProvider key={user.id} enabled={isManager}>
      <div className="mobile-bar">
        <button className="btn ghost icon" onClick={() => setOpen((o) => !o)} aria-label={t('nav.open_navigation')}>
          <MenuIcon />
        </button>
        <Link to="/" className="brand" style={{ padding: 0 }}>
          <BrandLogo height={22} />
        </Link>
      </div>
      <div className={`app ${railOn ? 'rail' : ''}`}>
        {open && <div className="sidebar-backdrop" onClick={() => setOpen(false)} aria-hidden />}
        <aside className={`sidebar ${open ? 'open' : ''} ${railOn ? 'rail' : ''}`}>
          <div className="sidebar-inner">
            <div className="sidebar-head">
              <Link to="/" className="brand" title={railOn ? t('nav.questionnaires') : undefined}>
                {railOn ? <BrandMark size={30} /> : <BrandLogo height={26} />}
              </Link>
              {collapseButton}
            </div>

            {can('survey.create') && (
              <Link to="/surveys/new" className="btn primary sidebar-cta" title={railOn ? t('nav.new_questionnaire') : undefined}>
                <Plus /> <span className="cta-text">{t('nav.new_questionnaire')}</span>
              </Link>
            )}

            <nav className="nav" aria-label={t('nav.main')}>
              {isManager && (
                <div className="nav-group">
                  <div className="nav-section">{t('nav.workspace')}</div>
                  <NavLink to="/surveys" className="nav-link" title={railOn ? t('nav.questionnaires') : undefined}>
                    <ClipboardList /> <span className="nav-text">{t('nav.questionnaires')}</span>
                  </NavLink>
                  <AssistantNavLink rail={railOn} />
                  {can('respondents.manage') && (
                    <NavLink to="/respondents" className="nav-link" title={railOn ? t('nav.respondents') : undefined}>
                      <Users /> <span className="nav-text">{t('nav.respondents')}</span>
                    </NavLink>
                  )}
                </div>
              )}
              {(can('users.manage') || can('audit.view') || can('settings.manage') || isAdmin) && (
                <div className="nav-group">
                  <div className="nav-section">{t('nav.administration')}</div>
                  {can('users.manage') && (
                    <NavLink to="/admin/users" className="nav-link" title={railOn ? t('nav.users_roles') : undefined}>
                      <UserCog /> <span className="nav-text">{t('nav.users_roles')}</span>
                    </NavLink>
                  )}
                  {can('users.manage') && (
                    <NavLink to="/admin/invitations" className="nav-link" title={railOn ? t('nav.invitations') : undefined}>
                      <MailPlus /> <span className="nav-text">{t('nav.invitations')}</span>
                    </NavLink>
                  )}
                  {isAdmin && (
                    <NavLink to="/admin/roles" className="nav-link" title={railOn ? t('nav.roles') : undefined}>
                      <ShieldCheck /> <span className="nav-text">{t('nav.roles')}</span>
                    </NavLink>
                  )}
                  {can('audit.view') && (
                    <NavLink to="/admin/audit" className="nav-link" title={railOn ? t('nav.audit') : undefined}>
                      <History /> <span className="nav-text">{t('nav.audit')}</span>
                    </NavLink>
                  )}
                  {can('settings.manage') && (
                    <NavLink to="/admin/settings" className="nav-link" title={railOn ? t('nav.settings') : undefined}>
                      <Settings /> <span className="nav-text">{t('nav.settings')}</span>
                    </NavLink>
                  )}
                </div>
              )}
            </nav>

            <div className="sidebar-lang">
              <span className="sidebar-lang-label">{t('lang.label')}</span>
              <LanguageSwitcher compact />
            </div>

            <div className="user-card" style={{ marginTop: 0 }}>
              <NavLink to="/account" className="user-link" title={t('nav.account_settings')}>
                <Avatar user={user} size={32} />
                <span className="meta">
                  <span className="name">{user.name || user.email}</span>
                  <span className="role">{user.title || roleLabel(t, user.role, user.roleName)}</span>
                </span>
              </NavLink>
              <div className="user-actions">
                <button
                  className="icon-btn"
                  onClick={() => setTheme(dark ? 'light' : 'dark')}
                  aria-label={dark ? t('nav.switch_light') : t('nav.switch_dark')}
                  title={dark ? t('nav.light_mode') : t('nav.dark_mode')}
                >
                  {dark ? <Sun /> : <Moon />}
                </button>
                <button className="icon-btn" onClick={logout} aria-label={t('nav.sign_out')} title={t('nav.sign_out')}>
                  <LogOut />
                </button>
              </div>
            </div>
          </div>
        </aside>
        <main className="main">
          <Outlet />
        </main>
      </div>
      <AssistantPanel />
    </AssistantProvider>
  );
}

export function Topbar({ title, crumbs, children }: { title: ReactNode; crumbs?: { to: string; label: string }[]; children?: ReactNode }) {
  const { t } = useI18n();
  return (
    <div className="mb-16">
      {crumbs && (
        <nav className="breadcrumb" aria-label={t('nav.breadcrumb')}>
          {crumbs.map((c, i) => (
            <span key={c.to} className="row gap-8">
              {i > 0 && <span>/</span>}
              <Link to={c.to}>{c.label}</Link>
            </span>
          ))}
        </nav>
      )}
      <div className="topbar" style={{ marginBottom: 0 }}>
        <h1 className="ellipsis">{title}</h1>
        <div className="spacer" />
        {children}
      </div>
    </div>
  );
}
